/* eslint-disable no-console */
import { Check } from "@phosphor-icons/react"
import { useVirtualizer } from "@tanstack/react-virtual"
import Link from "next/link"
import {
  Children,
  cloneElement,
  createContext,
  type HTMLAttributes,
  isValidElement,
  memo,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { createPortal } from "react-dom"
import { twMerge } from "tailwind-merge"
import { Icon } from "../../icon/Icon"
import BaseSearchInput from "./SearchInput"

// Utils

/**
 * Essentially a scrollIntoView handler but gives extra padding to the top and bottom of the target element
 */

const scrollIntoViewWithPadding = (
  scrollElement: HTMLElement,
  targetElement: Element
) => {
  const targetElementRect = targetElement.getBoundingClientRect()
  const scrollElementRect = scrollElement.getBoundingClientRect()
  const padding = 4

  // Scroll so the top of the target element is at the top of the scroll element
  if (targetElementRect.top < scrollElementRect.top) {
    const newScrollTop =
      targetElementRect.top -
      scrollElementRect.top +
      scrollElement.scrollTop -
      padding
    scrollElement.scrollTo({
      top: newScrollTop,
    })
    // Scroll so the bottom of the target element is at the bottom of the scroll element
  } else if (targetElementRect.bottom > scrollElementRect.bottom) {
    const newScrollTop =
      targetElementRect.bottom -
      scrollElementRect.top -
      scrollElementRect.height +
      scrollElement.scrollTop +
      padding

    scrollElement.scrollTo({
      top: newScrollTop,
    })
  }
}

const getListItems = (parentElement: HTMLElement) => {
  const listItems = parentElement.querySelectorAll(
    "[data-item-id]:not([data-disabled])"
  )
  return Array.from(listItems)
}

// Given a list of items, filter and sort them based on the search term
export const filterAndSortItems = <T,>(
  items: T[],
  search: string,
  getValue: (item: T) => string,
  getKeywords?: (item: T) => string[] | undefined
): T[] => {
  const searchLower = search.toLowerCase()
  const scoredItems = items.map((item) => {
    const value = getValue(item).toLowerCase()
    const keywords = getKeywords?.(item) || []

    let score = 0
    if (value.startsWith(searchLower)) {
      score = 1
    } else if (value.includes(" ")) {
      const words = value.split(" ")
      if (words.some((word) => word.toLowerCase().startsWith(searchLower))) {
        score = 0.9
      } else if (value.includes(searchLower)) {
        score = 0.5
      }
    } else if (value.includes(searchLower)) {
      score = 0.5
    }

    // Check keywords for matches. Scored slightly lower because we want to prioritize exact matches
    if (score === 0 && keywords.length > 0) {
      if (
        keywords.some((keyword) =>
          keyword.toLowerCase().startsWith(searchLower)
        )
      ) {
        score = 0.8
      } else if (
        keywords.some((keyword) => keyword.toLowerCase().includes(searchLower))
      ) {
        score = 0.4
      }
    }

    return {
      item,
      score,
    }
  })

  const sortedItems = scoredItems.sort((a, b) => b.score - a.score)
  const result = sortedItems.filter((child) => child.score > 0)
  return result.map((child) => child.item)
}

const getItemValueFromChildren = (children: React.ReactNode) => {
  if (typeof children === "string") {
    return children
  } else if (Array.isArray(children)) {
    const stringChildren = children.filter((child) => typeof child === "string")
    if (stringChildren.length > 0) {
      return stringChildren.join(" ")
    }
  }
  return null
}

// Contexts

const SearchContext = createContext<{
  search: string
  setSearch: (search: string) => void
}>({
  search: "",
  setSearch: () => {},
})

export const useSearchContext = () => {
  const context = useContext(SearchContext)
  if (!context) {
    throw new Error(
      "useSearchContext must be used within a SearchContextProvider"
    )
  }
  return context
}

const ItemContext = createContext<{
  containerRef: React.RefObject<HTMLDivElement> | null
  focusItem: (id: string | null) => void
  selectionMode?: "single" | "multiple"
  highlightSelectedItems: () => void
  highlightItem: (id: string) => void
  handleSelectItem: (id: string) => void
  onClickItem?: (id: string) => void
}>({
  containerRef: null,
  focusItem: () => {},
  selectionMode: "single",
  highlightSelectedItems: () => {},
  highlightItem: () => {},
  handleSelectItem: () => {},
  onClickItem: () => {},
})

const useItemContext = () => {
  const context = useContext(ItemContext)
  if (!context) {
    throw new Error("useItemContext must be used within a ItemContextProvider")
  }
  return context
}

// Components

export const Divider = ({
  className,
  ...props
}: { className?: string } & HTMLAttributes<HTMLDivElement>) => {
  return <div className={twMerge("tt-divider", className)} {...props} />
}

export const EmptyState = ({
  children = "No results",
  className,
}: {
  children?: React.ReactNode
  className?: string
}) => {
  return <div className={twMerge("tt-empty-state", className)}>{children}</div>
}

const GroupHeader = ({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) => {
  return (
    <div data-group-header className={twMerge("tt-group-header", className)}>
      {children}
    </div>
  )
}

const Group = ({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) => {
  return (
    <div data-group className={className}>
      {children}
    </div>
  )
}

const SelectionIndicator = ({
  children = <Icon as={Check} width={14} height={14} />,
  className,
}: {
  children?: React.ReactNode
  className?: string
}) => {
  return (
    <div className={twMerge("tt-selection-indicator", className)}>
      {children}
    </div>
  )
}

export type ItemProps = Omit<HTMLAttributes<HTMLDivElement>, "onClick"> & {
  id: string
  disabled?: boolean
  value?: string
  keywords?: string[]
  onClick?: (id: string) => void
  // Useful if you want to prevent the item from triggering a global onClickItem event
  preventGlobalOnClickItem?: boolean
  // Useful if you want to prevent the item from triggering a global handleSelectItem event
  preventGlobalHandleSelectItem?: boolean
  // Optional props for Link components
  href?: string
  target?: string
}

const Item = memo(
  ({
    id,
    children,
    className,
    disabled,
    value: incomingValue,
    keywords,
    onClick,
    preventGlobalOnClickItem,
    preventGlobalHandleSelectItem,
    href,
    target,
  }: ItemProps) => {
    const {
      focusItem,
      handleSelectItem,
      onClickItem,
      selectionMode,
      highlightItem,
    } = useItemContext()

    const value = getItemValueFromChildren(children) || incomingValue

    if (!value) {
      console.warn(
        "If Item children doesn't contain a string, you must provide a `value` prop (item with id: ",
        id,
        ")"
      )
    }

    useEffect(() => {
      if (selectionMode) {
        highlightItem(id)
      }
    }, [highlightItem, id, selectionMode])

    const Element = href ? Link : "div"
    const linkProps = href ? { href, target, tabIndex: -1 } : {}

    return (
      // @ts-ignore -- Link props
      <Element
        data-item-id={id}
        data-item-value={value}
        data-item-keywords={keywords ? keywords.join(",") : undefined}
        className={twMerge("tt-list-item", className)}
        data-disabled={disabled || undefined}
        onMouseMove={() => {
          if (disabled) {
            return
          }
          focusItem(id)
        }}
        {...linkProps}
        onClick={() => {
          if (disabled) {
            return
          }

          onClick?.(id)
          if (!preventGlobalOnClickItem) {
            onClickItem?.(id)
          }
          if (selectionMode !== undefined && !preventGlobalHandleSelectItem) {
            handleSelectItem(id)
          }
        }}
      >
        {children}
      </Element>
    )
  }
)
Item.displayName = "Item"

const SearchInput = ({
  placeholder = "Search",
  value,
  onChange,
  onKeyDown,
  innerRef,
  autoFocus = true,
  size = "medium",
  className,
  borderStyle = "bottom",
  inputProps,
}: {
  placeholder?: string
  value?: string
  onChange?: (value: string) => void
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void
  innerRef?: React.RefObject<HTMLInputElement>
  autoFocus?: boolean
  size?: "small" | "medium" | "large"
  className?: string
  borderStyle?: "default" | "bottom"
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>
}) => {
  const isControlled = value != undefined
  const { search, setSearch } = useSearchContext()

  useEffect(() => {
    if (value != undefined) {
      setSearch(value)
    }
  }, [value, setSearch])

  return (
    <BaseSearchInput
      innerRef={innerRef}
      placeholder={placeholder}
      value={search}
      onChange={(value) => {
        if (!isControlled) {
          setSearch(value)
        }
        onChange?.(value)
      }}
      onKeyDown={(e) => {
        if (e.key === "Escape" && (e.target as HTMLInputElement).value !== "") {
          e.preventDefault()
          e.stopPropagation()
          setSearch("")
          return
        }
        onKeyDown?.(e)
      }}
      autoFocus={autoFocus}
      size={size}
      className={twMerge(
        borderStyle === "bottom"
          ? "!rounded-none !border-t-0 !border-l-0 !border-r-0 !bg-transparent !border-border-default"
          : "",
        className
      )}
      inputProps={inputProps}
    />
  )
}

const List = ({
  children,
  className,
  shouldFilter = true,
  style,
}: {
  children: React.ReactNode
  className?: string
  shouldFilter?: boolean
  style?: React.CSSProperties
}) => {
  const baseChildren = useRef(children)
  const { search } = useSearchContext()
  const { containerRef, focusItem, highlightSelectedItems } = useItemContext()

  // An optimization we could make is caching the item values so that scoring is faster
  const filteredChildren = useMemo(() => {
    if (!shouldFilter) {
      return children
    }

    if (search) {
      // Filter out non-Item children
      const childrenArray = Children.toArray(children).filter(
        (child): child is React.ReactElement<ItemProps> => {
          return (
            isValidElement(child) &&
            typeof child.type === "object" &&
            // Previously we checked child.type === Item, but hot reloading
            // was causing the check to fail ¯\_(ツ)_/¯
            (child.type as { displayName?: string }).displayName === "Item"
          )
        }
      )

      const sortedAndFilteredItems = filterAndSortItems(
        childrenArray,
        search,
        (child) => {
          return (
            // We know that if we can't derive a value from the children, the
            // value prop should be set
            getItemValueFromChildren(child.props.children) ||
            child.props.value ||
            ""
          )
        },
        (child) => child.props.keywords
      )

      if (sortedAndFilteredItems.length === 0) {
        return <EmptyState />
      }

      return sortedAndFilteredItems.map((child) => {
        return (
          // @ts-ignore - TODO: Fix types
          <Item
            key={child.props.id}
            id={child.props.id}
            value={child.props.value || undefined}
            keywords={child.props.keywords}
            onClick={child.props.onClick}
          >
            {child.props.children}
          </Item>
        )
      })
    }

    baseChildren.current = children

    return Children.toArray(children).map((child) => {
      if (typeof child === "object" && "type" in child && child.type === Item) {
        return (
          <Item
            key={child.props.id}
            id={child.props.id}
            value={child.props.value || undefined}
            keywords={child.props.keywords}
            onClick={child.props.onClick}
          >
            {child.props.children}
          </Item>
        )
      } else {
        return child
      }
    })
  }, [search, children, shouldFilter])

  useEffect(() => {
    requestAnimationFrame(() => {
      if (!containerRef || !containerRef.current) return

      const firstListItem = containerRef.current.querySelector("[data-item-id]")
      const firstItemId = firstListItem?.getAttribute("data-item-id")

      if (firstItemId && firstListItem) {
        focusItem(firstItemId)
        scrollIntoViewWithPadding(containerRef.current, firstListItem)
      }
    })
  }, [search, focusItem, containerRef, children])

  useLayoutEffect(() => {
    highlightSelectedItems()
  }, [filteredChildren, highlightSelectedItems])

  return (
    <div
      ref={containerRef}
      className={twMerge("tt-item-container", className)}
      style={style}
    >
      {filteredChildren}
    </div>
  )
}

/**
 * A helper function to get find the child with the most text. Used to find the theoretical widest
 * child in the list so we can use that to set the width of the virtualized list.
 */

function getChildWithMostText(
  children: Array<Exclude<React.ReactNode, boolean | null | undefined>>
) {
  const countTextNodes = (node: React.ReactNode): number => {
    if (typeof node === "string") {
      return node.length
    }
    if (typeof node === "number") {
      return node.toString().length
    }
    if (Array.isArray(node)) {
      // @ts-ignore - TODO: Fix types
      return node.reduce((acc, child) => acc + countTextNodes(child), 0)
    }
    if (node && typeof node === "object" && "props" in node) {
      // @ts-ignore - TODO: Fix types
      return countTextNodes(node.props.children)
    }
    return 0
  }

  const textCounts = Children.toArray(children).map((item) =>
    countTextNodes(item)
  )
  const indexOfMax = textCounts.indexOf(Math.max(...textCounts))
  return Children.toArray(children)[indexOfMax]
}

const VirtualizedList = ({
  children,
  className,
  shouldFilter = true,
  style,
}: {
  children: React.ReactNode
  className?: string
  shouldFilter?: boolean
  style?: React.CSSProperties
}) => {
  const baseChildren = useRef(children)
  const { search } = useSearchContext()
  const { containerRef, focusItem, highlightSelectedItems } = useItemContext()
  const hiddenMeasuringRef = useRef<HTMLDivElement>(null)

  const filteredChildren = useMemo(() => {
    if (!shouldFilter) {
      return children
    }

    if (search) {
      // Filter out non-Item children
      const childrenArray = Children.toArray(children).filter(
        (child): child is React.ReactElement<ItemProps> =>
          typeof child === "object" && "type" in child && child.type === Item
      )

      const sortedAndFilteredItems = filterAndSortItems(
        childrenArray,
        search,
        (child) => {
          return (
            // We know that if we can't derive a value from the children, the
            // value prop should be set
            getItemValueFromChildren(child.props.children) ?? child.props.value!
          )
        },
        (child) => child.props.keywords
      )

      if (sortedAndFilteredItems.length === 0) {
        return <EmptyState />
      }

      return sortedAndFilteredItems.map((child) => {
        return (
          // @ts-ignore - TODO: Fix types
          <Item
            key={child.props.id}
            id={child.props.id}
            value={child.props.value || undefined}
            keywords={child.props.keywords}
            onClick={child.props.onClick}
          >
            {child.props.children}
          </Item>
        )
      })
    }

    baseChildren.current = children

    return Children.toArray(children).map((child) => {
      if (typeof child === "object" && "type" in child && child.type === Item) {
        return (
          <Item
            key={child.props.id}
            id={child.props.id}
            value={child.props.value || undefined}
            keywords={child.props.keywords}
            onClick={child.props.onClick}
          >
            {child.props.children}
          </Item>
        )
      } else {
        return child
      }
    })
  }, [search, children, shouldFilter])

  const childrenArray = useMemo(
    () => Children.toArray(filteredChildren),
    [filteredChildren]
  )

  const widestChild = useMemo(() => {
    return getChildWithMostText(childrenArray)
  }, [childrenArray])

  const rowVirtualizer = useVirtualizer({
    count: childrenArray.length,
    getScrollElement: () => containerRef?.current ?? null,
    estimateSize: () => 35,
    overscan: 30,
    getItemKey: (index) => {
      if (
        typeof childrenArray[index] === "object" &&
        "type" in childrenArray[index] &&
        childrenArray[index].type === Item
      ) {
        return childrenArray[index]?.props.id
      }
      return index
    },
  })

  useEffect(() => {
    rowVirtualizer.scrollToIndex(0)
    requestAnimationFrame(() => {
      if (!containerRef || !containerRef.current) return

      const firstListItem = containerRef.current.querySelector("[data-item-id]")
      const firstItemId = firstListItem?.getAttribute("data-item-id")

      if (firstItemId && firstListItem) {
        focusItem(firstItemId)
        scrollIntoViewWithPadding(containerRef.current, firstListItem)
      }
    })
  }, [search, focusItem, containerRef, filteredChildren, rowVirtualizer])

  useLayoutEffect(() => {
    highlightSelectedItems()
  }, [filteredChildren, highlightSelectedItems])

  useLayoutEffect(() => {
    if (hiddenMeasuringRef.current && containerRef?.current) {
      const measuredWidth =
        hiddenMeasuringRef.current.getBoundingClientRect().width + 8
      containerRef.current.style.minWidth = `${measuredWidth}px`
    }
  }, [hiddenMeasuringRef, widestChild, containerRef])

  return (
    <div
      ref={containerRef}
      className={twMerge("tt-item-container", className)}
      style={style}
    >
      <div
        style={{
          height: rowVirtualizer.getTotalSize(),
          position: "relative",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            transform: `translateY(${
              rowVirtualizer.getVirtualItems()[0]?.start ?? 0
            }px)`,
          }}
        >
          {rowVirtualizer.getVirtualItems().map((virtualRow) => {
            return (
              <div
                key={virtualRow.key}
                data-index={virtualRow.index}
                ref={rowVirtualizer.measureElement}
              >
                {childrenArray[virtualRow.index]}
              </div>
            )
          })}
        </div>
      </div>
      {/* Hack to measure the width of the widest child */}
      {widestChild &&
        createPortal(
          <div
            ref={hiddenMeasuringRef}
            data-id="virtual-scroll-measure-element"
            style={{
              position: "fixed",
              visibility: "hidden",
              top: 0,
              right: 0,
              zIndex: -1,
            }}
          >
            {cloneElement(widestChild as React.ReactElement, {
              disabled: true,
            })}
          </div>,
          document.body
        )}
    </div>
  )
}

export type SelectionProps =
  | {
      selectionMode: "single"
      selectedId?: string
      onChangeSelectedId?: (id: string | null) => void
      selectedIds?: never
      onChangeSelectedIds?: never
    }
  | {
      selectionMode: "multiple"
      selectedId?: never
      onChangeSelectedId?: never
      selectedIds?: string[] | null
      onChangeSelectedIds?: (ids: string[] | null) => void
    }
  | {
      selectionMode?: undefined
      selectedId?: never
      onChangeSelectedId?: undefined
      selectedIds?: never
      onChangeSelectedIds?: undefined
    }

type SearchState = {
  search?: string
  onChangeSearch?: (search: string) => void
}

type SearchableListProps = {
  children: React.ReactNode
  onClickItem?: (id: string) => void
} & SelectionProps &
  SearchState

/**
 * Note: If you used controlled state, you should memoize the children
 */

export const SearchableList = ({
  children,
  // Controlled state (optional)
  search: externalSearch,
  onChangeSearch,
  // Selection state - todo
  selectionMode,
  selectedId,
  onChangeSelectedId,
  selectedIds,
  onChangeSelectedIds,
  onClickItem,
}: SearchableListProps) => {
  const focusedItemId = useRef<string | null>(null)
  const selectedItemId = useRef<string | null>(null)
  const selectedItemIds = useRef<string[] | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [internalSearch, setInternalSearch] = useState<string>("")
  const search = externalSearch ?? internalSearch
  const setSearch = onChangeSearch ?? setInternalSearch

  const highlightFocusedItem = useCallback(() => {
    if (!containerRef.current || !focusedItemId.current) return

    const itemElements = getListItems(containerRef.current)
    itemElements.forEach((element) => {
      element.removeAttribute("data-focus-visible")
    })

    const focusedElement = itemElements.find(
      (element) =>
        element.getAttribute("data-item-id") === focusedItemId.current
    )
    if (focusedElement) {
      focusedElement.setAttribute("data-focus-visible", "true")
    }
  }, [focusedItemId, containerRef])

  const highlightItem = useCallback(
    (id: string) => {
      if (!containerRef.current) return

      const itemElements = getListItems(containerRef.current)
      const element = itemElements.find(
        (el) => el.getAttribute("data-item-id") === id
      )
      if (!element) return

      const selection =
        selectionMode === "single"
          ? [selectedItemId.current]
          : selectedItemIds.current

      if (selection?.includes(id)) {
        element.setAttribute("data-selected", "true")
      } else {
        element.removeAttribute("data-selected")
      }
    },
    [selectedItemIds, selectionMode, selectedItemId]
  )

  const highlightSelectedItems = useCallback(() => {
    if (!containerRef.current) return
    const selection =
      selectionMode === "single"
        ? [selectedItemId.current]
        : selectedItemIds.current

    if (!selection) return

    const itemElements = getListItems(containerRef.current)
    itemElements.forEach((element) => {
      const elementId = element.getAttribute("data-item-id")
      if (elementId && selection.includes(elementId)) {
        element.setAttribute("data-selected", "true")
      } else {
        element.removeAttribute("data-selected")
      }
    })
  }, [selectedItemIds, selectionMode, selectedItemId])

  const focusItem = useCallback(
    (id: string | null) => {
      focusedItemId.current = id

      if (id) {
        highlightFocusedItem()
      }
    },
    [highlightFocusedItem]
  )

  const handleSelectItem = useCallback(
    (id: string) => {
      if (selectionMode === "single") {
        const newSelectedId = selectedItemId.current === id ? null : id
        onChangeSelectedId?.(newSelectedId)
        selectedItemId.current = newSelectedId
      } else {
        const newSelectedIds = (selectedItemIds.current || []).includes(id)
          ? (selectedItemIds.current || []).filter((item) => item !== id)
          : [...(selectedItemIds.current || []), id]
        onChangeSelectedIds?.(newSelectedIds)
        selectedItemIds.current = newSelectedIds
      }

      // Update the dom
      highlightSelectedItems()
    },
    [
      onChangeSelectedId,
      onChangeSelectedIds,
      selectionMode,
      highlightSelectedItems,
    ]
  )

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!containerRef.current) return

      if (e.key === "ArrowDown") {
        e.preventDefault()
        const listItems = getListItems(containerRef.current)
        const itemIds = listItems.map((item) =>
          item.getAttribute("data-item-id")
        )

        if (!focusedItemId.current) {
          const firstItemId = itemIds[0]
          if (firstItemId) {
            focusItem(firstItemId)
          }
        } else {
          const nextIndex = itemIds.indexOf(focusedItemId.current) + 1
          const nextItemId = itemIds[nextIndex]
          if (nextItemId) {
            const nextElement = listItems[nextIndex]
            if (nextElement) {
              focusItem(nextItemId)
              scrollIntoViewWithPadding(containerRef.current, nextElement)
            }
          }
        }
      } else if (e.key === "ArrowUp") {
        e.preventDefault()
        const listItems = getListItems(containerRef.current)
        const itemIds = listItems.map((item) =>
          item.getAttribute("data-item-id")
        )

        if (!focusedItemId.current) {
          // Focus first item
          const firstItemId = itemIds[0]
          if (firstItemId) {
            focusItem(firstItemId)
          }
        } else {
          const previousIndex = itemIds.indexOf(focusedItemId.current) - 1
          const previousItemId = itemIds[previousIndex]
          if (previousItemId) {
            const previousElement = listItems[previousIndex]
            if (previousElement) {
              focusItem(previousItemId)
              scrollIntoViewWithPadding(containerRef.current, previousElement)
            }
          }
        }
      } else if (e.key === "Enter") {
        e.preventDefault()
        const focusedItem = focusedItemId.current
        const element = containerRef.current?.querySelector(
          `[data-item-id="${focusedItem}"]`
        )

        if (e.metaKey && element) {
          const href = (element as HTMLElement).getAttribute("href")
          if (href) {
            window.open(href, "_blank")
          }
        } else if (element) {
          ;(element as HTMLElement).click()
        }
      }
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => {
      window.removeEventListener("keydown", handleKeyDown)
    }
  }, [focusItem])

  useEffect(() => {
    if (selectedIds !== undefined && selectionMode === "multiple") {
      selectedItemIds.current = selectedIds
    } else if (selectedId !== undefined && selectionMode === "single") {
      selectedItemId.current = selectedId
    }

    requestAnimationFrame(() => {
      highlightSelectedItems()
    })
  }, [selectedIds, selectedId, selectionMode, highlightSelectedItems])

  const searchContext = useMemo(() => {
    return {
      search,
      setSearch,
    }
  }, [search, setSearch])

  const itemContext = useMemo(() => {
    return {
      containerRef,
      focusItem,
      selectionMode,
      handleSelectItem,
      highlightSelectedItems,
      highlightItem,
      onClickItem,
    }
  }, [
    focusItem,
    selectionMode,
    handleSelectItem,
    highlightSelectedItems,
    highlightItem,
    onClickItem,
  ])

  return (
    <SearchContext.Provider value={searchContext}>
      <ItemContext.Provider value={itemContext}>
        {children}
      </ItemContext.Provider>
    </SearchContext.Provider>
  )
}

SearchableList.Divider = Divider
SearchableList.Group = Group
SearchableList.GroupHeader = GroupHeader
SearchableList.Item = Item
SearchableList.List = List
SearchableList.SearchInput = SearchInput
SearchableList.SelectionIndicator = SelectionIndicator
SearchableList.VirtualizedList = VirtualizedList
SearchableList.EmptyState = EmptyState
